<script lang="ts">
	//Formatters
	import { format } from 'svelte-i18n';
	//Helpers
	import { get_local_ip, real_id } from '../../../helpers/interface_helpers';
	import { onWifiResponse } from '../NetworkComponents/network_helpers';
	import { capFirstChar } from '../../../helpers/utilities';
	//Services
	import { localReq, postReq } from '../../../services/http';
	//Stores
	import { devices_status } from 'src/stores/device-runtime/status';
	import { getDeviceStatus, getDeviceSettings } from 'src/stores/device-runtime/status-selectors';
	import { showNewLayout } from 'src/stores/toDelete/new_layout';
	//Design system components
	import { Checkbox, Button, Alert } from '@Shelly/shelly-design-system';
	//Components
	import NetworkModal from '../NetworkComponents/NetworkModal.svelte';
	import SSIDForm from '../NetworkComponents/SSIDForm.svelte';
	import StaticIPAddressForm from '../NetworkComponents/StaticIPAddressForm.svelte';
	import WifiClientStatus from '../NetworkComponents/WifiClientStatus.svelte';
	//Types
	import type { SettingRequest } from '../../../types/requests/settings/setting';
	import type { Device } from '../../../types/Device';
	import { getContext, onDestroy } from 'svelte';
	import type { Writable } from 'svelte/store';
	import type { SidepanelStickyBottomProps } from 'src/components/Sidepanel/sidepanel-types';
	import { classMap } from '@Shelly/shelly-design-system/helpers/component-utils';

	$: settingsStore = getDeviceSettings(id);
	export let device: Device;
	export let isOffline;

	const id = real_id(device.id),
		settings = $settingsStore;

	let ssid = settings?.wifi_sta1?.ssid,
		enabled = settings?.wifi_sta1?.enabled,
		static_ip = settings?.wifi_sta1?.ipv4_method === 'static',
		waitForResponse = false;
	const staticSettings = {
		...settings?.wifi_sta1,
		ip:
			!static_ip && devices_status.getByRealId(id)?.wifi_sta?.ssid == ssid
				? devices_status.getByRealId(id)?.wifi_sta?.ip
				: settings?.wifi_sta1?.ip,
	};

	let ssidForm: SSIDForm,
		staticIpAddressForm: StaticIPAddressForm,
		modal: NetworkModal,
		postReqData: {
			id: string;
			staid: number;
			enabled: number;
			ipv4_method?: string;
			ssid?: string;
			key?: string;
			ip?: string;
			mask?: string;
			gateway?: string;
			dns?: string;
		};

	const connect = async () => {
			waitForResponse = true;
			const local_ip = get_local_ip(id);
			let res: SettingRequest;
			if (local_ip) {
				delete postReqData.id;
				delete postReqData.staid;
				res = await localReq(device, `${local_ip}/settings/sta1`, 'post', postReqData);
			} else {
				res = await postReq(`/device/settings/wifi_sta`, postReqData);
			}
			onWifiResponse(res, ssid, enabled, device);
			waitForResponse = false;
		},
		//The SSID and password are required to the given request
		validate = () => {
			postReqData = {
				id,
				staid: 1,
				enabled: Number(enabled),
			};
			if (enabled) {
				const ssidParams = ssidForm.validate();
				if (!ssidParams) return false;
				postReqData.ssid = ssidParams.ssid;
				postReqData.key = ssidParams.pass;
				postReqData.ipv4_method = 'dhcp';
				if (static_ip) {
					const staticIpAddress = staticIpAddressForm.validate(settings?.cloud.enabled);
					if (!staticIpAddress) return false;
					postReqData.ipv4_method = 'static';
					postReqData.ip = staticIpAddress.ip;
					postReqData.mask = staticIpAddress.mask;
					postReqData.gateway = staticIpAddress.gw;
					postReqData.dns = staticIpAddress.dns;
				}
			}
			modal.open();
		};

	$: hasAnotherNetworkSetUp = settings?.wifi_sta?.enabled;

	const stickyBottomAction =
		getContext<Writable<SidepanelStickyBottomProps | null>>('stickyBottomAction');

	$: if (stickyBottomAction) {
		if ($showNewLayout) {
			stickyBottomAction.set({ onClick: validate, loading: waitForResponse, disabled: isOffline });
		} else {
			stickyBottomAction.set(null);
		}
	}

	onDestroy(() => {
		stickyBottomAction?.set(null);
	});
</script>

{#if $showNewLayout}
	<Checkbox size="l" class="mb-xs" bind:checked={enabled} disabled={isOffline} id="toggle-q86xipm">
		{capFirstChar($format('sub_settings_title_wifi_client_backup', { default: 'wi-fi 2' }))}
	</Checkbox>

	<WifiClientStatus
		{isOffline}
		{...{ id, ssid: $settingsStore?.wifi_sta1.ssid }}
		classes={classMap({
			'mb-s-plus': isOffline || get_local_ip(id),
		})}
	/>

	<SSIDForm
		removeBottomPadding
		removePaddings
		{isOffline}
		disableForm={!enabled || isOffline}
		bind:this={ssidForm}
		bind:ssid
		showAlert={!get_local_ip(id)}
	/>

	<hr class="mt-m mb-m" />
	<StaticIPAddressForm
		removeBottomMargin
		removePaddings
		disabledForm={!enabled || isOffline}
		settings={staticSettings}
		bind:this={staticIpAddressForm}
		bind:open={static_ip}
	/>
{:else}
	<Checkbox size="l" class="mb-s" bind:checked={enabled} disabled={isOffline} id="checkbox-q86xipm">
		{capFirstChar($format('option_enable', { default: 'enable' }))}
	</Checkbox>

	<hr class="mb-m mt-s" />

	<WifiClientStatus {isOffline} {...{ id, ssid: $settingsStore?.wifi_sta1.ssid }} />

	<SSIDForm
		{isOffline}
		disableForm={!enabled || isOffline}
		bind:this={ssidForm}
		bind:ssid
		showAlert={!get_local_ip(id)}
	/>

	<StaticIPAddressForm
		disabledForm={!enabled || isOffline}
		settings={staticSettings}
		bind:this={staticIpAddressForm}
		bind:open={static_ip}
	/>

	<Button size="m" width="full" on:click={validate} loading={waitForResponse} disabled={isOffline}>
		{$format('button_save', { default: 'Save' })}
	</Button>
{/if}

<NetworkModal
	btnTexts={{
		cancelText: '',
		saveText: enabled ? $format('button_save') : $format('disable'),
	}}
	title={enabled
		? $format('save_wifi_settings_title', {
				default: 'Save Wi-Fi {type} Settings?',
				values: { type: '2' },
			})
		: $format('disable_wifi_settings_title', {
				default: 'Disable Wi-Fi {type}?',
				values: { type: '2' },
			})}
	bind:this={modal}
	on:confirm={connect}
	{isOffline}
	hideSlotAdditionalInfo={!enabled}
>
	{#if enabled}
		{$format('wifi_prompt_modal_enable', {
			values: { ssid },
			default: 'Your device will connect to ‘{ssid}’ when the signal is strong enough.',
		})}
	{:else if hasAnotherNetworkSetUp}
		{$format('wifi_prompt_modal_disabled_auto_connect', {
			values: { value: 'Wi-Fi 2', secondValue: 'Wi-Fi 1' },
			default:
				'By disabling ‘{value}’ your device will automatically connect to the configured ‘{secondValue}’ network to maintain connectivity.',
		})}
	{:else}
		{$format('wifi_prompt_modal_disabled', {
			values: { value: 'Wi-Fi 2' },
			default: 'By disabling ‘{value}’ you may lose connection to the device.',
		})}
	{/if}

	<div class="mt-m" slot="additionalInfo">
		<Alert
			type="warning"
			heading={$format('require_device_reboot_prompt', {
				default: 'Note that this action might require a device reboot.',
			})}
		/>
	</div>
</NetworkModal>
